/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import {
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
  Bar,
  Line,
  ComposedChart
} from 'recharts'
import { useState, useMemo, SetStateAction, Dispatch, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/shared/shadcn/components/ui/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip
  // ChartTooltipContent
} from '@/shared/shadcn/components/ui/chart'
import Select from '@/shared/components/Select/Select'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { DynamicTooltip } from '../DynamicTooltip/DynamicTooltip'

type ChartDataType = {
  date: string
  [key: string]: any
}

interface BarChartProps {
  title: string
  description?: string | React.ReactNode
  chartData: ChartDataType[]
  dataKey: keyof ChartDataType
  lineChartData?: ChartDataType[]
  lineChartDataKey?: string
  hideGap?: boolean
  flex?: number | 'auto'
  setStartDate: Dispatch<SetStateAction<string>>
  isLoading: boolean
  loadingPlaceholder: JSX.Element
}

export function BarChart({
  title,
  description,
  chartData,
  dataKey,
  lineChartData,
  lineChartDataKey,
  hideGap,
  flex,
  setStartDate,
  isLoading,
  loadingPlaceholder
}: BarChartProps) {
  const [timeRange, setTimeRange] = useState('90d')

  let daysToSubtract
  switch (timeRange) {
    case '7d':
      daysToSubtract = 7
      break
    case '30d':
      daysToSubtract = 30
      break
    default:
      daysToSubtract = 90
  }

  const { t } = useTranslation()

  const options = useMemo(
    () => [
      { label: t('DASHBOARD.LAST_3_MONTHS'), value: '90d' },
      { label: t('DASHBOARD.LAST_30_DAYS'), value: '30d' },
      { label: t('DASHBOARD.LAST_7_DAYS'), value: '7d' }
    ],
    [t]
  )

  const selected = useMemo(
    () => options.find(o => o.value === timeRange) ?? options[0],
    [options, timeRange]
  )

  const endDate = '2025-12-01'

  const newStartDate = useMemo(() => {
    const [y, m, d] = endDate.split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d))
    dt.setUTCDate(dt.getUTCDate() - daysToSubtract)
    return dt.toISOString().slice(0, 10)
  }, [endDate, daysToSubtract])

  useEffect(() => {
    setStartDate(newStartDate)
  }, [newStartDate, setStartDate])

  const chartConfig: ChartConfig = useMemo(
    () => ({
      [dataKey as string]: {
        label: t('DASHBOARD.CHART.AVERAGE_VALUE'),
        color: 'var(--chart-1)'
      },
      ...(lineChartDataKey
        ? {
            [lineChartDataKey]: {
              label: t('DASHBOARD.CHART.LINE_TREND'),
              color: 'var(--foreground)'
            }
          }
        : {})
    }),
    [dataKey, lineChartDataKey, t]
  )

  const mergedData = useMemo(() => {
    const map = new Map<string, any>()

    chartData.forEach(item => {
      const color =
        (item[dataKey] as number) < 10
          ? 'var(--muted-foreground)'
          : chartConfig[dataKey as string].color

      map.set(item.date, { ...item, [`${dataKey as string}_color`]: color })
    })

    lineChartData?.forEach(item => {
      const existing = map.get(item.date) || { date: item.date }
      map.set(item.date, { ...existing, ...item })
    })

    return Array.from(map.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )
  }, [chartConfig, chartData, dataKey, lineChartData])

  // Deliberately cropped to the data range instead of anchored at zero: these
  // values sit in a narrow band far from 0, so a zero baseline renders every
  // day as the same full-height bar. The trade-off is that bar length no longer
  // reads as absolute magnitude, only as the day-to-day difference. Exact
  // values stay available in the tooltip.
  const yAxisDomain = useMemo<[number, number]>(() => {
    const values = mergedData.flatMap(item =>
      [item[dataKey as string], lineChartDataKey && item[lineChartDataKey]]
        .filter(value => typeof value === 'number')
        .map(Number)
    )

    if (!values.length) return [0, 10]

    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = max - min

    // A flat series has no differences to show, fall back to a padded band so
    // the bars stay visible instead of collapsing to nothing.
    if (span === 0) return [Math.max(0, min - 10), max + 10]

    return [Math.max(0, min - span * 0.25), max + span * 0.15]
  }, [mergedData, dataKey, lineChartDataKey])

  const flexClass = (() => {
    switch (flex) {
      case 1:
        return 'flex-1'
      case 2:
        return 'flex-2'
      case 'auto':
        return 'flex-auto'
      default:
        return 'flex-1'
    }
  })()

  return (
    <Card className={`pt-0 relative ${flexClass} pb-5 gap-0 min-w-0`}>
      {isLoading && loadingPlaceholder}
      <CardHeader
        className={`flex flex-col border-b py-5 ${hideGap ? 'gap-0' : 'gap-6'}`}
      >
        <div className="flex items-start justify-between w-full">
          <CardTitle>{title}</CardTitle>
          <Select
            options={options}
            value={selected}
            onChange={option => setTimeRange((option as OptionType).value)}
            minWidth="9rem"
            maxWidth="11rem"
            hideClearIcon
            widthFitContent
          />
        </div>
        {!isLoading && description && (
          <CardDescription className="w-full flex justify-between pr-[10rem]">
            {description}
          </CardDescription>
        )}
      </CardHeader>
      {!isLoading && (
        <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6 h-full">
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-full w-full"
          >
            <ComposedChart data={mergedData}>
              <CartesianGrid
                vertical={false}
                stroke="color-mix(in oklab, var(--border) 100%, transparent)"
              />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={value => {
                  const date = new Date(value)
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })
                }}
              />
              <YAxis hide domain={yAxisDomain} />
              <ChartTooltip
                cursor={false}
                content={
                  <DynamicTooltip
                    dataKey={dataKey as string}
                    lineChartDataKey={lineChartDataKey}
                    chartConfig={chartConfig}
                  />
                }
              />

              <Bar
                dataKey={dataKey}
                name={String(chartConfig[dataKey as string].label)}
                fill={chartConfig[dataKey as string].color}
                radius={4}
                isAnimationActive={false}
              >
                {mergedData.map(entry => (
                  <Cell
                    key={`cell-${entry.date}`}
                    fill={
                      (entry[dataKey] as number) < 10
                        ? 'var(--muted-foreground)'
                        : chartConfig[dataKey as string].color
                    }
                  />
                ))}
              </Bar>

              {lineChartDataKey && (
                <Line
                  dataKey={lineChartDataKey}
                  name={String(chartConfig[lineChartDataKey].label)}
                  type="monotone"
                  stroke="var(--foreground)"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      )}
    </Card>
  )
}
